import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../components/ui/alert-dialog';
import { extrasAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, Loader2, Edit2, X, CheckSquare } from 'lucide-react';

export const AdminExtras = () => {
    const navigate = useNavigate();
    const [presets, setPresets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);  // null = closed, 'new' = new, id = editing
    const [form, setForm] = useState({ name: '', default_amount: '', sort_order: 0 });
    const [saving, setSaving] = useState(false);

    useEffect(() => { load(); }, []);

    const load = async () => {
        try {
            const res = await extrasAPI.listPresets();
            setPresets(res.data);
        } catch { toast.error('Failed to load extras'); }
        finally { setLoading(false); }
    };

    const openNew = () => {
        setForm({ name: '', default_amount: '', sort_order: presets.length + 1, show_on_quote: true });
        setEditingId('new');
    };

    const openEdit = (p) => {
        setForm({ name: p.name, default_amount: String(p.default_amount), sort_order: p.sort_order, show_on_quote: p.show_on_quote !== false });
        setEditingId(p.id);
    };

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error('Name required'); return; }
        setSaving(true);
        try {
            const payload = { name: form.name.trim(), default_amount: parseFloat(form.default_amount) || 0, sort_order: parseInt(form.sort_order) || 0, active: true, show_on_quote: form.show_on_quote !== false };
            if (editingId === 'new') {
                const res = await extrasAPI.createPreset(payload);
                setPresets(prev => [...prev, res.data]);
                toast.success('Extra added');
            } else {
                const res = await extrasAPI.updatePreset(editingId, payload);
                setPresets(prev => prev.map(p => p.id === editingId ? res.data : p));
                toast.success('Extra updated');
            }
            setEditingId(null);
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        try {
            await extrasAPI.deletePreset(id);
            setPresets(prev => prev.filter(p => p.id !== id));
            toast.success('Extra removed');
        } catch { toast.error('Failed to remove'); }
    };

    if (loading) return (
        <Layout>
            <div className="space-y-4 max-w-2xl mx-auto">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-64 rounded-xl" />
            </div>
        </Layout>
    );

    return (
        <Layout>
            <div className="space-y-4 max-w-2xl mx-auto">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} className="h-10 w-10">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex-1">
                        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">Job Extras</h1>
                        <p className="text-slate-500 text-sm">Site extras shown as tick boxes on each job</p>
                    </div>
                    <Button onClick={openNew} className="h-10 bg-red-600 hover:bg-red-700 text-white" data-testid="add-extra-btn">
                        <Plus className="h-4 w-4 mr-2" /> Add Extra
                    </Button>
                </div>

                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                    These appear as tick boxes on each job (above Manual Adjustments). When ticked with an amount, they're included in the quote total and shown as line items on the PDF.
                </div>

                {/* Add / Edit form */}
                {editingId && (
                    <Card className="border-red-200 bg-red-50/20">
                        <CardContent className="p-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <p className="font-semibold text-sm text-slate-800">{editingId === 'new' ? 'New Extra' : 'Edit Extra'}</p>
                                <button onClick={() => setEditingId(null)}><X className="h-4 w-4 text-slate-400" /></button>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2 space-y-1">
                                    <Label className="text-xs font-bold uppercase text-slate-500">Name *</Label>
                                    <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                        placeholder="e.g. Waste Removal" className="h-10 border-slate-300" data-testid="extra-name-input" />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs font-bold uppercase text-slate-500">Default Amount (£)</Label>
                                    <Input type="number" step="0.01" min="0" value={form.default_amount}
                                        onChange={e => setForm(p => ({ ...p, default_amount: e.target.value }))}
                                        placeholder="0.00" className="h-10 border-slate-300" data-testid="extra-amount-input" />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs font-bold uppercase text-slate-500">Sort Order</Label>
                                    <Input type="number" min="0" value={form.sort_order}
                                        onChange={e => setForm(p => ({ ...p, sort_order: e.target.value }))}
                                        className="h-10 border-slate-300" />
                                </div>
                                <div className="col-span-2 flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                    <div>
                                        <p className="text-sm font-semibold text-slate-800">Show on client quote</p>
                                        <p className="text-xs text-slate-500">Uncheck to absorb silently into the total</p>
                                    </div>
                                    <button type="button" onClick={() => setForm(p => ({ ...p, show_on_quote: !p.show_on_quote }))}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.show_on_quote !== false ? 'bg-red-600' : 'bg-slate-300'}`}>
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.show_on_quote !== false ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                            </div>
                            <Button onClick={handleSave} disabled={saving} className="w-full h-10 bg-red-600 hover:bg-red-700 text-white" data-testid="save-extra-btn">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                                Save Extra
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {/* List */}
                {presets.length === 0 ? (
                    <Card><CardContent className="p-8 text-center text-slate-400">
                        <CheckSquare className="h-10 w-10 mx-auto mb-3 opacity-30" />
                        <p className="font-medium">No extras defined</p>
                    </CardContent></Card>
                ) : (
                    <Card className="bg-white border-slate-200">
                        <CardContent className="p-0">
                            <div className="divide-y divide-slate-100">
                                {presets.map(p => (
                                    <div key={p.id} className="flex items-center justify-between px-4 py-3" data-testid={`extra-row-${p.id}`}>
                                        <div className="flex-1">
                                            <p className="font-medium text-sm text-slate-900">{p.name}</p>
                                            <p className="text-xs text-slate-500">
                                                Default: £{p.default_amount.toFixed(2)} &bull;{' '}
                                                <span className={p.show_on_quote !== false ? 'text-green-600' : 'text-amber-600'}>
                                                    {p.show_on_quote !== false ? 'Shown on quote' : 'Absorbed (hidden)'}
                                                </span>
                                            </p>
                                        </div>
                                        <div className="flex gap-1">
                                            <button onClick={() => openEdit(p)} className="w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100">
                                                <Edit2 className="h-3.5 w-3.5" />
                                            </button>
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <button className="w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50">
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Remove "{p.name}"?</AlertDialogTitle>
                                                        <AlertDialogDescription>It will no longer appear on new jobs.</AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                        <AlertDialogAction onClick={() => handleDelete(p.id)} className="bg-red-600">Remove</AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
};
